import { useAtom } from 'jotai';
import { useMemo } from 'react';

import { useGetApiV1ExamGrid } from '@/shared/api/generated';
import type { ExamGridListItemViewModel } from '@/shared/api/generated/types/ExamGridListItemViewModel';
import type { GetApiV1ExamGridQueryParams } from '@/shared/api/generated/types/examEvaluationController/GetApiV1ExamGrid';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

import { examFiltersAtom } from '../store/atoms/examFiltersAtom';
import type { ExamFilters } from '../types/exam';

const PAGE_SIZE = 20;

type PagedResponse = {
  currentPage?: number;
  data?: ExamGridListItemViewModel[];
  totalCount?: number;
  totalPages?: number;
};

function unpackResponse(raw: unknown): {
  items: ExamGridListItemViewModel[];
  totalCount: number;
  totalPages: number;
} {
  if (Array.isArray(raw)) {
    return { items: raw as ExamGridListItemViewModel[], totalCount: raw.length, totalPages: 1 };
  }
  const paged = raw as PagedResponse | undefined;
  const items = unwrapPagedResponse<ExamGridListItemViewModel>(raw);
  return {
    items,
    totalCount: paged?.totalCount ?? items.length,
    totalPages: paged?.totalPages ?? 1,
  };
}

function buildApiParams(filters: ExamFilters): GetApiV1ExamGridQueryParams {
  return {
    ...(filters.discipline !== 'all' && { DisciplineId: Number(filters.discipline) }),
    ...(filters.search && { Title: filters.search }),
    ...(filters.type !== 'all' && { ExamType: Number(filters.type) }),
    ...(filters.status !== 'all' && { Active: filters.status === 'active' }),
    ...(filters.classRoomDisciplineStatus !== 'all' && {
      Status: Number(filters.classRoomDisciplineStatus),
    }),
    PageNumber: filters.page,
    PageSize: PAGE_SIZE,
  };
}

/**
 * Hook para listar provas com filtros e paginação server-side.
 *
 * Todos os filtros (título, tipo, status, disciplina, status de planejamento)
 * são enviados diretamente à API com os nomes corretos em PascalCase.
 * O campo `searchTrigger` força re-fetch ao clicar em "Pesquisar".
 *
 * @returns Dados de provas paginados com estado de loading/error
 *
 * @example
 * ```tsx
 * const { data, isLoading, totalCount, currentPage, totalPages } = useExamList();
 * ```
 */
export function useExamList() {
  const [filters] = useAtom(examFiltersAtom);

  const apiParams = useMemo(() => buildApiParams(filters), [filters]);

  // A queryKey inclui apenas page e searchTrigger — filtros ficam em apiParams
  // mas NÃO disparam re-fetch sozinhos. Somente clicar em "Pesquisar"
  // (que incrementa searchTrigger) ou mudar de página recarrega os dados.
  const query = useGetApiV1ExamGrid(apiParams, {
    query: {
      queryKey: ['exam-grid', filters.page, filters.searchTrigger],
    },
  });

  const { items, totalCount, totalPages } = useMemo(() => unpackResponse(query.data), [query.data]);

  return {
    data: items,
    isLoading: query.isLoading,
    isError: query.isError,
    totalCount,
    currentPage: filters.page,
    totalPages,
  } as const;
}

/**
 * Hook para gerenciar filtros e paginação da listagem de provas.
 *
 * @returns Filtros atuais e funções para atualizar, resetar e paginar
 */
export function useExamFilters() {
  const [filters, setFilters] = useAtom(examFiltersAtom);

  const updateFilters = (partial: Partial<ExamFilters>) => {
    setFilters(prev => ({ ...prev, ...partial, page: 1 }));
  };

  const applyFilters = (partial: Partial<ExamFilters>) => {
    setFilters(prev => ({ ...prev, ...partial, page: 1, searchTrigger: prev.searchTrigger + 1 }));
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      type: 'all',
      discipline: 'all',
      status: 'all',
      classRoomDisciplineStatus: 'all',
      page: 1,
      searchTrigger: 0,
    });
  };

  const setPage = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  return { filters, updateFilters, applyFilters, resetFilters, setPage } as const;
}
